import playbackOrchestrator from "../../services/playbackOrchestrator/playbackOrchestrator";
import "../../services/lesson/lessonService";
import settingsService from "../../services/settings/settingsService";
import {subscribeToNotesEvents} from "../../services/eventBus/notes/notesEventBus";
import {NotesEventTypes} from "../../services/eventBus/notes/NotesEventTypes";
import {SegmentModeEnum} from "../../models/view/lessonPage/SegmentModeEnum";
import {setProgressBarVisibility} from "../../components/lesson/progressBar/ProgressBarLogic";
import {lessonPlayerStore} from "./LessonPlayerStore";
import {
  setScrollTypeAndVideoPositionBasedOnOrientation
} from "../../services/lesson/lessonService";
import {isBirdsEyeKeyboardVisible} from "../../models/view/lessonPage/LessonPlayerSettings";
import {getNotationLayoutToApplyFromSelectedNotationLayout} from "../../services/lesson/lessonUtility";

/**
 * This file should not exist. It is a remnant of how Redux was used in the past. Eventually we want:
 * Move everything out of this file and put it one of these:
 * - LessonStore
 * - lessonUtility.ts file
 * - LessonPage (or its hook)
 * - ControlBar (or its hook)
 */

function hasPlaybackStarted(): boolean {
  const isPlaying = lessonPlayerStore.getIsPlaying();
  const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();
  return isPlaying || isCountInPlaying;
}

// Consider moving into the lesson page hook together with play() stop() and pause()
export function play(): void {
  const wasScrollingAdjusted = adjustSSScrollingIfNeeded();
  if (wasScrollingAdjusted) {
    // When scrolling is adjusted, it causes Soundslice to reload. When Soundslice reloads, it will call 'play' again.
    return;
  }

  playbackOrchestrator.onPlay_pressed();
  updateControlBarVisibilityAndSetProgressBarVisibility();
}

// Consider moving into the lesson page hook together with play() stop() and pause()
export function stop(): void {
  playbackOrchestrator.onStop_pressed();
  updateControlBarVisibilityAndSetProgressBarVisibility();
}

// Consider moving into the lesson page hook together with play() stop() and pause()
export function pause(): void {
  playbackOrchestrator.onPause_pressed();
  updateControlBarVisibilityAndSetProgressBarVisibility();
}

function updateControlBarVisibilityAndSetProgressBarVisibility(): void {
  updateControlBarVisibility();
  setProgressBarVisibility();
}

function updateControlBarVisibility() {
  const isPlaying = lessonPlayerStore.getIsPlaying();
  const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();
  const segment = lessonPlayerStore.getSegment();

  const shouldRenderAsPlaying = (isPlaying || isCountInPlaying) && !!segment;

  if(shouldRenderAsPlaying) {
    settingsService.setShouldHideSegmentDescription(segment.id, true);
  }

  lessonPlayerStore.setIsControlBarIsPartiallyHidden(shouldRenderAsPlaying);
}

/**
 * When segment notation is playing on a mobile device and the screen is rotated, the listeners to adjust the
 * layout of the video/notation for the new orientation are disabled. To get the user back to the ideal layout,
 * need to adjust the layout when play / stop are pressed.
 * @returns true if the scrolling was adjusted, false if it was not.
 */
function adjustSSScrollingIfNeeded(): boolean {
  const segment = lessonPlayerStore.getSegment();
  if (segment) {
    const isSegmentNotAVideo = segment.mode !== SegmentModeEnum.followAlong && segment.mode !== SegmentModeEnum.play;
    const isPlaybackStopped = !hasPlaybackStarted();

    const appliedNotationLayout = lessonPlayerStore.getAppliedNotationLayout();
    const layoutThatShouldBeApplied = getNotationLayoutToApplyFromSelectedNotationLayout();

    const shouldAdjustScrolling =
      isPlaybackStopped
      && isSegmentNotAVideo
      && (appliedNotationLayout !== layoutThatShouldBeApplied);

    if (shouldAdjustScrolling) {
      // The orientation at this point does not match the desired scroll method, so we need to change it. However,
      // changing the scroll method will cause the iFrame to reload. When the iFrame reloads, the playback stops.
      // We don't want that. So, set a flag here to tell the iFrame to start playing when it's ready so the user
      // doesn't have to press play again.
      lessonPlayerStore.setShouldPlayWhenSSReady(true);
      setScrollTypeAndVideoPositionBasedOnOrientation(segment);
      return true;
    } else {
      return false;
    }
  }

  return false;
}

subscribeToNotesEvents((message: NotesEventTypes)=> {
  switch(message.type) {
    case "playback_reset": // fall through to playback_ended
    case "playback_ended":
      //we need to show the control bar if playback stops because the user reached the end of the segment.
      lessonPlayerStore.setIsControlBarIsPartiallyHidden(false);
      break;
  }
})

export async function increaseSpeed() {
  await playbackOrchestrator.increaseSpeed();
  settingsService.partiallySetUserPreferencesForControlBar({speedPercent: playbackOrchestrator.playbackControlState.speed});
}
export async function changeSpeed(speed: number) {
  await playbackOrchestrator.setSpeed(speed);
  settingsService.partiallySetUserPreferencesForControlBar({speedPercent: playbackOrchestrator.playbackControlState.speed});
}
export async function decreaseSpeed() {
  await playbackOrchestrator.decreaseSpeed();
  settingsService.partiallySetUserPreferencesForControlBar({speedPercent: playbackOrchestrator.playbackControlState.speed});
}

export function toggleFretboardVisibility() {
  // Guard: Make sure we have a segment
  const segment = lessonPlayerStore.getSegment();
  if (!segment) {
    return;
  }
  
  const isVisible = !isBirdsEyeKeyboardVisible(segment.lessonPlayerSettings, segment.mode);
  playbackOrchestrator.setIsKeyboardVisible(isVisible);

  switch (segment.mode) {
    case SegmentModeEnum.followAlong:
      settingsService.partiallySetUserPreferencesForControlBar({isBirdsEyeKeyboardEnabledForSegmentsWithBothVideoAndNotation: isVisible});
      break;
    case SegmentModeEnum.play:
      settingsService.partiallySetUserPreferencesForControlBar({isBirdsEyeKeyboardEnabledForSegmentsWithVideoOnly: isVisible});
      break;
    default:
      settingsService.partiallySetUserPreferencesForControlBar({isBirdsEyeKeyboardEnabled: isVisible});
  }
}

export function toggleStaffFingeringsVisibility() {
  const segment = lessonPlayerStore.getSegment();
  if (!segment) {
    return;
  }

  const isVisible = !segment.lessonPlayerSettings.staffFingeringsVisible;
  playbackOrchestrator.setFingeringVisibility(isVisible);
  settingsService.partiallySetUserPreferencesForControlBar({isFingeringEnabled: isVisible});
}

function isUserOnLessonPlayerPage(): boolean {
  const segment = lessonPlayerStore.getSegment();
  return !!segment;
}

export function toggleNoteNamesVisibility() {
  const segment = lessonPlayerStore.getSegment();
  if (!segment) {
    return;
  }

  const isVisible = !segment.lessonPlayerSettings.noteNamesVisible;
  playbackOrchestrator.setNoteNameVisibility(isVisible);
  settingsService.partiallySetUserPreferencesForControlBar({isNoteNamesEnabled: isVisible});
}

export function toggleRhythmCountsVisibility() {
  return () => {
    const segment = lessonPlayerStore.getSegment();
    if (!segment) {
      return;
    }

    const isVisible = !segment.lessonPlayerSettings.displayRhythmCounts;
    playbackOrchestrator.setRhythmCountVisibility(isVisible);
    settingsService.partiallySetUserPreferencesForControlBar({areRhythmCountsEnabled: isVisible});
  }
}

export function togglePlayheadVisibility() {
  if (!isUserOnLessonPlayerPage()) { return; }

  const isPlayheadVisible = !lessonPlayerStore.getHidePlayheadWhilePlaying();
  lessonPlayerStore.setHidePlayheadWhilePlaying(isPlayheadVisible);
  playbackOrchestrator.setIsPlayheadVisibleWhilePlaying(isPlayheadVisible);
  settingsService.partiallySetUserPreferencesForControlBar({hidePlayheadDuringPlayback: isPlayheadVisible});
}

export function toggleHighlightCurrentMeasure() {
  if (!isUserOnLessonPlayerPage()) {
    return;
  }

  const isCurrentMeasureHighlighted = !lessonPlayerStore.getHighlightCurrentMeasure();
  lessonPlayerStore.setHighlightCurrentMeasure(isCurrentMeasureHighlighted);
  playbackOrchestrator.setIsCurrentMeasureHighlighted(isCurrentMeasureHighlighted);
  settingsService.partiallySetUserPreferencesForControlBar({isCurrentMeasureHighlighted: isCurrentMeasureHighlighted});
}

export function toggleProportionalNotation() {
  if (!isUserOnLessonPlayerPage()) {
    return;
  }

  const isNotationProportional = !lessonPlayerStore.getIsProportionalSpacingEnabled();
  playbackOrchestrator.setIsProportionalNotationEnabled(isNotationProportional);
}

export function changePageCount(pageCount: number) {
  if (!isUserOnLessonPlayerPage()) { return; }

  playbackOrchestrator.setPageCount(pageCount);
  settingsService.partiallySetUserPreferencesForControlBar({pageCount: pageCount});
}

export function changeNotationLayout(layout: number) {
  playbackOrchestrator.setNotationLayout(layout);
  settingsService.partiallySetUserPreferencesForControlBar({selectedNotationLayout: layout});
}

export function setHorizontalScrollAndVideoPositionBasedOnOrientation() {
  const segment = lessonPlayerStore.getSegment();
  setScrollTypeAndVideoPositionBasedOnOrientation(segment);
}
